import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-[#B8860B] mb-3">{title}</h2>
    <div className="text-[#F4F1EA]/85 leading-relaxed space-y-3">{children}</div>
  </section>
);

export const PrivacyPolicy = () => {
  const lastUpdated = 'February 5, 2026';
  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      {/* Hero */}
      <section className="relative h-56 flex items-center justify-center overflow-hidden border-b border-[#B8860B]/20">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(5, 8, 20, 0.85), rgba(5, 8, 20, 0.95)), url('https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?auto=format&fit=crop&w=2000&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container mx-auto px-4 z-10 text-center text-[#F4F1EA]">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-sm text-[#B8860B]">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-3xl text-[#F4F1EA]">
        <p className="text-[#F4F1EA]/85 mb-10 leading-relaxed">
          Creek Bottom Ranch &amp; Resort ("we," "us," or "our") respects your privacy. This Privacy
          Policy explains what information we collect, how we use it, and your rights. By using
          creekbottomranch.com or making a reservation, you agree to this policy.
        </p>

        <Section title="1. Information We Collect">
          <p>We collect the following information when you book, rent, contact us, or subscribe:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Name, email address, phone number, billing address</li>
            <li>Reservation and rental details (dates, site type, equipment, add-ons)</li>
            <li>Payment information processed by Stripe (we do not store full card numbers)</li>
            <li>Communication preferences (e.g., SMS opt-in)</li>
            <li>Basic technical data (IP address, browser type) for security and analytics</li>
          </ul>
        </Section>

        <Section title="2. How We Use Your Information">
          <ul className="list-disc pl-6 space-y-1">
            <li>To process reservations, equipment rentals, and payments</li>
            <li>To send booking confirmations, reminders, and operational messages</li>
            <li>To respond to your inquiries and provide customer support</li>
            <li>To send marketing emails and texts only with your consent</li>
            <li>To comply with legal obligations and protect against fraud</li>
          </ul>
        </Section>

        <Section title="3. SMS / Text Messaging (Twilio)">
          <p>
            We use <strong>Twilio</strong> to send SMS messages related to your booking, equipment
            rental, or inquiry. Messages may include:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Booking and rental confirmations</li>
            <li>Check-in reminders and arrival instructions</li>
            <li>Important operational updates (weather, road closures, schedule changes)</li>
            <li>Responses to text-based questions you initiate</li>
          </ul>
          <p>
            <strong>Consent:</strong> By checking the SMS consent box during checkout or
            inquiry submission, you authorize Creek Bottom Ranch &amp; Resort to send text messages
            to the phone number you provided.
          </p>
          <p>
            <strong>Frequency:</strong> Message frequency varies based on your activity (typically
            1-5 messages per booking).
          </p>
          <p>
            <strong>Cost:</strong> Message and data rates may apply, charged by your wireless
            carrier. Creek Bottom Ranch &amp; Resort does not charge for messages.
          </p>
          <p>
            <strong>Opt-out:</strong> Reply <strong>STOP</strong> to any message to unsubscribe.
            Reply <strong>HELP</strong> for assistance. You can also email
            info@creekbottomranch.com to opt out.
          </p>
          <p>
            Mobile carriers are not liable for delayed or undelivered messages.
          </p>
        </Section>

        <Section title="4. Information Sharing">
          <p>We do <strong>not</strong> sell, rent, or trade your personal information. We share data only with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Stripe</strong> – for secure payment processing</li>
            <li><strong>Twilio</strong> – for SMS delivery</li>
            <li><strong>Email service providers</strong> – for transactional and confirmation emails</li>
            <li>Law enforcement or government agencies when legally required</li>
          </ul>
          <p>
            Mobile information (phone numbers and SMS opt-in data) is <strong>never</strong>
            shared with third parties for marketing purposes.
          </p>
        </Section>

        <Section title="5. Data Security">
          <p>
            We use industry-standard safeguards including encrypted database connections, secure
            HTTPS transport, and PCI-compliant payment processing through Stripe. No system is 100%
            secure; in the unlikely event of a breach we will notify affected guests as required by law.
          </p>
        </Section>

        <Section title="6. Your Rights">
          <ul className="list-disc pl-6 space-y-1">
            <li>Request a copy of the personal data we hold about you</li>
            <li>Request correction or deletion of your data</li>
            <li>Withdraw SMS or email consent at any time</li>
            <li>Lodge a complaint with a data protection authority</li>
          </ul>
          <p>
            To exercise these rights, email{' '}
            <a className="text-[#B8860B] hover:underline" href="mailto:info@creekbottomranch.com">
              info@creekbottomranch.com
            </a>
            .
          </p>
        </Section>

        <Section title="7. Cookies & Analytics">
          <p>
            We use minimal first-party cookies for essential site functionality (login sessions on
            the admin dashboard, cart-style state during booking). We do not use third-party
            advertising trackers.
          </p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            Creek Bottom Ranch &amp; Resort is an adults-only (21+) property. Our website is not
            directed to children under 13, and we do not knowingly collect personal information
            from children.
          </p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Material changes will be posted on
            this page with a revised "Last updated" date.
          </p>
        </Section>

        <Section title="10. Contact Us">
          <p>
            <strong>Creek Bottom Ranch &amp; Resort</strong>
            <br />
            2724 Private Road 8160
            <br />
            Lovelady, TX 75851
            <br />
            Phone:{' '}
            <a className="text-[#B8860B] hover:underline" href="tel:8327140101">
              (832) 714-0101
            </a>
            <br />
            Email:{' '}
            <a className="text-[#B8860B] hover:underline" href="mailto:info@creekbottomranch.com">
              info@creekbottomranch.com
            </a>
          </p>
        </Section>

        <p className="text-sm text-[#F4F1EA]/60 mt-12 border-t border-[#B8860B]/20 pt-6">
          See also our{' '}
          <Link to="/terms" className="text-[#B8860B] hover:underline">
            Terms &amp; Conditions
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
